import { useTranslation } from "react-i18next"

import type { FairUseOverviewRow } from "@/api/admin/fair-use"

import { Check, MoreHorizontal, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Progress } from "@/components/ui/progress"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { clampPercentForDisplay } from "@/features/fair-use/fair-use-utils"
import { cn } from "@/lib/utils"

import {
  fairUseInitials,
  fairUseRowActions,
  fairUseRowBadgeVariant,
  fairUseRowProgressClass,
  fairUseRowStatus,
  fairUseRowStatusCopy,
  formatBudgetUsage,
  formatGrantDate,
  formatRelativeAge,
  formatSekAmount,
} from "./fair-use-ops-utils"
import { fairUseDisplayName } from "./fair-use-utils"

type FairUseUsageTableProps = {
  rows: FairUseOverviewRow[]
  /** The month's `enabled` flag: false means nobody was warned or stopped. */
  enforced: boolean
  isPlatformAdmin: boolean
  blockCapSek: number
  isResolvingRequest: boolean
  onApprove: (row: FairUseOverviewRow) => void
  onDeny: (row: FairUseOverviewRow) => void
  onTopUpUser: (row: FairUseOverviewRow) => void
  onRevokeGrant: (row: FairUseOverviewRow, grantId: string) => void
}

export function FairUseUsageTable({
  rows,
  enforced,
  isPlatformAdmin,
  blockCapSek,
  isResolvingRequest,
  onApprove,
  onDeny,
  onTopUpUser,
  onRevokeGrant,
}: FairUseUsageTableProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const topUpLabel = formatSekAmount(blockCapSek, locale)

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[220px]">
              {t("adminDashboard.fairUse.ops.table.user", "User")}
            </TableHead>
            <TableHead className="min-w-[150px]">
              {t("adminDashboard.fairUse.ops.table.plan", "Plan")}
            </TableHead>
            <TableHead className="min-w-[190px]">
              {t("adminDashboard.fairUse.ops.table.usage", "Usage this month")}
            </TableHead>
            <TableHead className="min-w-[120px]">
              {t("adminDashboard.fairUse.ops.table.status", "Status")}
            </TableHead>
            <TableHead className="w-48 text-right">
              {t("adminDashboard.fairUse.ops.table.actions", "Actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const status = fairUseRowStatus(row, enforced)
            const displayName = fairUseDisplayName(row)
            const plan = row.tier ?? "standard"
            const pending = row.pending_request
            const askedAgo = pending ? formatRelativeAge(pending.ts, locale) : undefined
            // Department first, e-mail as the fallback: the point of the line
            // is "which person is this", and an oid never answers that.
            const secondary = [
              row.profile?.department || row.profile?.email,
              askedAgo
                ? t("adminDashboard.fairUse.ops.table.askedAgo", "asked {{ago}}", { ago: askedAgo })
                : undefined,
            ]
              .filter(Boolean)
              .join(" · ")

            return (
              <TableRow key={row.user} className={cn(pending && "bg-warning/10")}>
                <TableCell className="max-w-[320px] align-top">
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                    >
                      {fairUseInitials(row)}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold" title={row.user}>
                        {displayName}
                      </div>
                      {secondary && (
                        <div className="text-muted-foreground truncate text-xs">{secondary}</div>
                      )}
                      {pending?.motivation && (
                        <blockquote className="border-warning text-muted-foreground mt-1.5 border-l-2 pl-2 text-xs italic">
                          {pending.motivation}
                        </blockquote>
                      )}
                    </div>
                  </div>
                </TableCell>

                <TableCell className="align-top">
                  {/* Read-only here: the plan is ASSIGNED on Fair use →
                      Users & licences, so this table has exactly one job —
                      the month. */}
                  <span className="text-sm">{t(`adminDashboard.fairUse.plans.${plan}`, plan)}</span>
                </TableCell>

                <TableCell className="align-top">
                  <div className="space-y-1.5 pt-1">
                    <Progress
                      className="h-1.5"
                      indicatorClassName={fairUseRowProgressClass(status)}
                      value={clampPercentForDisplay(row.percent)}
                    />
                    <div className="text-muted-foreground text-xs tabular-nums">
                      {formatBudgetUsage(row, locale)}
                    </div>
                  </div>
                </TableCell>

                <TableCell className="align-top">
                  <Badge variant={fairUseRowBadgeVariant(status)}>
                    {t(fairUseRowStatusCopy(status).key, fairUseRowStatusCopy(status).defaultValue)}
                  </Badge>
                </TableCell>

                <TableCell className="text-right align-top">
                  {pending ? (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        disabled={isResolvingRequest}
                        size="sm"
                        onClick={() => onApprove(row)}
                      >
                        <Check className="mr-1 size-3.5" />
                        {t("adminDashboard.fairUse.ops.approve", "Approve +{{amount}} SEK", {
                          amount: topUpLabel,
                        })}
                      </Button>
                      <Button
                        className="text-destructive hover:text-destructive"
                        disabled={isResolvingRequest}
                        size="sm"
                        variant="ghost"
                        onClick={() => onDeny(row)}
                      >
                        <X className="mr-1 size-3.5" />
                        {t("adminDashboard.fairUse.ops.deny", "Deny")}
                      </Button>
                    </div>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={t(
                            "adminDashboard.fairUse.ops.table.moreActions",
                            "More actions for {{user}}",
                            { user: displayName }
                          )}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      {/* Every entry comes from fairUseRowActions(), which also
                          owns the platform-only gate on withdrawals — so the
                          rule that is unit-tested is the rule that ships, not a
                          second copy of it written in JSX. */}
                      <DropdownMenuContent align="end">
                        {fairUseRowActions({ row, isPlatformAdmin, blockCapSek }).map((action) =>
                          action.kind === "top-up" ? (
                            <DropdownMenuItem key="top-up" onSelect={() => onTopUpUser(row)}>
                              {t("adminDashboard.fairUse.ops.rowTopUp", "Top up +{{amount}} SEK", {
                                amount: formatSekAmount(action.sek, locale),
                              })}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              key={action.grantId}
                              className="text-destructive focus:text-destructive"
                              onSelect={() => onRevokeGrant(row, action.grantId)}
                            >
                              {t(
                                "adminDashboard.fairUse.ops.rowRevoke",
                                "Withdraw top-up (+{{amount}} SEK, {{date}})",
                                {
                                  amount: formatSekAmount(action.sek, locale),
                                  date: formatGrantDate(action.ts, locale),
                                }
                              )}
                            </DropdownMenuItem>
                          )
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
